import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";
import type { GitLabBranch, GitLabProject } from "@/api/types";

export function useGitLabProjects(q: string) {
  return useQuery({
    queryKey: keys.projects(q),
    queryFn: async () =>
      (
        await api.get<GitLabProject[]>(
          `/gitlab/projects?q=${encodeURIComponent(q)}`,
        )
      ).data,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useGitLabProject(projectId: number | null) {
  // Guard against NaN / Infinity. Sessions created before a project
  // was picked leave repository_id = null, and Number("") === 0 is
  // also invalid. The picker used to send /gitlab/projects/NaN and
  // get a 422.
  const valid =
    projectId != null && Number.isFinite(projectId) && projectId > 0;
  return useQuery({
    queryKey: ["gitlab", "project", valid ? projectId : "idle"],
    enabled: valid,
    queryFn: async () =>
      (await api.get<GitLabProject>(`/gitlab/projects/${projectId}`)).data,
    staleTime: 5 * 60_000,
  });
}

export function useGitLabBranches(projectId: number | null, q: string) {
  return useQuery({
    queryKey: keys.branches(projectId, q),
    queryFn: async () =>
      (
        await api.get<GitLabBranch[]>(
          `/gitlab/projects/${projectId}/branches?q=${encodeURIComponent(q)}`,
        )
      ).data,
    enabled: projectId != null,
    placeholderData: keepPreviousData,
  });
}

export type RepositoryStatus = {
  id: string;
  status: "cloning" | "ready" | "failed" | string;
  failure_reason: string | null;
  /** ``classify_clone_failure`` code when ``status`` is ``failed``. */
  failure_code?: string | null;
  path_with_namespace: string;
  last_fetched_at: string | null;
};

export function useRepositoryStatus(repositoryId: string | null) {
  return useQuery({
    queryKey: ["gitlab", "repository-status", repositoryId ?? "idle"],
    enabled: Boolean(repositoryId),
    queryFn: async () =>
      (
        await api.get<RepositoryStatus>(
          `/gitlab/repositories/${repositoryId}/status`,
        )
      ).data,
    refetchInterval: (q) => (q.state.data?.status === "cloning" ? 2000 : false),
  });
}

// ---- Code sources (per-org SCM links) ----

/** Mirror of the backend ``SCM_KINDS`` (``app/services/scm/kinds.py``).
 *  Per-kind UI copy is keyed by ``Record<CodeSourceKind, …>`` maps (see
 *  ``@/lib/scmKinds``), so adding a kind here fails the build until
 *  every map has an entry for it. */
export type CodeSourceKind = "gitlab" | "github" | "bitbucket" | "azure_devops";

export type CodeSourceView = {
  id: string;
  slug: string;
  display_name: string;
  kind: CodeSourceKind;
  base_url: string;
  group_filter: string | null;
  ssh_host: string | null;
  ssh_public_key: string | null;
  ssh_fingerprint_sha256: string | null;
  gitlab_attribution_mode: string | null;
  is_default: boolean;
  has_pat_read: boolean;
  has_pat_write: boolean;
  /** ``true`` once ``ssh-keyscan`` has populated the source's pinned
   *  ``known_hosts`` bytes. Drives the "Re-scan host keys" button
   *  label — pre-pin it offers a fix for "Host key verification
   *  failed", post-pin it offers a way to recover from a host-key
   *  rotation. */
  has_pinned_known_hosts: boolean;
  /** The root org's GitLab source from the server configuration: it comes
   *  back at every restart, so it can't be deleted here. */
  env_managed?: boolean;
  created_at: string;
  updated_at: string;
};

const codeSourcesKey = (slug: string) =>
  ["orgs", slug, "code-sources"] as const;

export function useCodeSources(slug: string) {
  return useQuery({
    queryKey: codeSourcesKey(slug),
    queryFn: async () =>
      (await api.get<CodeSourceView[]>(`/orgs/${slug}/code-sources`)).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

export function useCreateCodeSource(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      display_name: string;
      kind: CodeSourceKind;
      base_url: string;
      slug?: string | null;
      group_filter?: string | null;
      ssh_host?: string | null;
      pat_read?: string | null;
      pat_write?: string | null;
      gitlab_attribution_mode?: "sudo" | "attribution" | "auto" | null;
      is_default?: boolean;
    }) =>
      (await api.post<CodeSourceView>(`/orgs/${slug}/code-sources`, payload))
        .data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: codeSourcesKey(slug) });
    },
  });
}

export function useUpdateCodeSource(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: string;
      display_name?: string;
      base_url?: string;
      group_filter?: string | null;
      ssh_host?: string | null;
      gitlab_attribution_mode?: "sudo" | "attribution" | "auto" | null;
      is_default?: boolean;
      pat_read?: string | null;
      pat_write?: string | null;
    }) => {
      const { id, ...body } = payload;
      const r = await api.patch<CodeSourceView>(
        `/orgs/${slug}/code-sources/${id}`,
        body,
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: codeSourcesKey(slug) });
    },
  });
}

export type ConnectionTestResult = {
  api_ok: boolean;
  api_detail: string | null;
  ssh_ok: boolean | null;
  ssh_detail: string | null;
  sampled_project: string | null;
  /** Why the SSH check failed, as a fixed code (``auth_rejected``,
   *  ``repo_access_denied``, ``host_key_unverified``, ``timeout``,
   *  ``ssh_unreachable``, ``blocked_host``, ``key_unavailable``,
   *  ``unknown``). Absent from older backends. */
  ssh_code?: string | null;
};

/** Connectivity probe: PAT against the provider API + deploy key
 *  against ``git ls-remote`` on the first visible project. Lets the
 *  admin see a green check before the first session. */
export function useTestCodeSourceConnection(slug: string) {
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<ConnectionTestResult>(
          `/orgs/${slug}/code-sources/${id}/test`,
        )
      ).data,
  });
}

export function useRotateCodeSourceSsh(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<CodeSourceView>(
          `/orgs/${slug}/code-sources/${id}/rotate-ssh`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: codeSourcesKey(slug) });
    },
  });
}

/** Re-run ``ssh-keyscan`` against the source's host and pin the
 *  fresh keys. Recovers from "Host key verification failed" on
 *  freshly-added sources or after a host-key rotation, without a
 *  deploy step. */
export function useRescanCodeSourceHostKeys(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<CodeSourceView>(
          `/orgs/${slug}/code-sources/${id}/rescan-host-keys`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: codeSourcesKey(slug) });
    },
  });
}

/** Pin operator-pasted ``known_hosts`` lines on the source. The
 *  escape hatch when ``ssh-keyscan`` can't reach the host (private /
 *  self-hosted git server behind the SSRF guard, unreachable, or a
 *  single-key-type host) so the automatic rescan can never pin —
 *  the operator runs ``ssh-keyscan`` themselves and pastes the
 *  output here. A malformed paste comes back as a 422 with detail
 *  ``invalid_known_hosts``; callers surface that inline. */
export function useSetCodeSourceHostKeys(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: string; known_hosts: string }) =>
      (
        await api.post<CodeSourceView>(
          `/orgs/${slug}/code-sources/${payload.id}/set-host-keys`,
          { known_hosts: payload.known_hosts },
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: codeSourcesKey(slug) });
    },
  });
}

/** Drop the row's pinned ``ssh_known_hosts`` bytes — recovery for
 *  the case where a rescan wrote bytes that don't match what the
 *  host actually presents. Subsequent clones fall back through the
 *  embedded provider defaults / deploy-wide known_hosts file. */
export function useClearCodeSourceHostKeys(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<CodeSourceView>(
          `/orgs/${slug}/code-sources/${id}/clear-host-keys`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: codeSourcesKey(slug) });
    },
  });
}

/** What deleting a source affects: the projects imported through it and
 *  the sessions using them (``GET …/code-sources/{id}/usage``). */
export type CodeSourceUsage = { projects: number; sessions: number };

export function useCodeSourceUsage(slug: string, id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...codeSourcesKey(slug), id, "usage"] as const,
    queryFn: async () =>
      (await api.get<CodeSourceUsage>(`/orgs/${slug}/code-sources/${id}/usage`))
        .data,
    enabled: Boolean(slug) && enabled,
    // Asked each time the confirm opens: the counts move with every session.
    staleTime: 0,
    gcTime: 0,
  });
}

export function useDeleteCodeSource(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/orgs/${slug}/code-sources/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: codeSourcesKey(slug) });
    },
  });
}


// ---- Code source projects + branches ----

export type CodeSourceProject = {
  id: string;
  path_with_namespace: string;
  name: string;
  default_branch: string;
  visibility: string;
};

export type CodeSourceBranch = {
  name: string;
  commit_sha: string;
  committed_date: string;
  protected: boolean;
};

const codeSourceProjectsKey = (
  orgSlug: string,
  sourceId: string,
  q: string,
) =>
  ["orgs", orgSlug, "code-sources", sourceId, "projects", q] as const;
const codeSourceBranchesKey = (
  orgSlug: string,
  sourceId: string,
  projectId: string,
  q: string,
) =>
  [
    "orgs",
    orgSlug,
    "code-sources",
    sourceId,
    "projects",
    projectId,
    "branches",
    q,
  ] as const;

/** Projects in display order for the query ``q`` that returned them. */
export function orderCodeSourceProjects(
  projects: CodeSourceProject[],
  q: string,
): CodeSourceProject[] {
  if (q.trim() !== "") return projects;
  return [...projects].sort((a, b) =>
    a.path_with_namespace.localeCompare(b.path_with_namespace, undefined, {
      sensitivity: "base",
    }),
  );
}

export function useCodeSourceProjects(
  orgSlug: string,
  sourceId: string | null,
  q: string,
) {
  return useQuery({
    queryKey: codeSourceProjectsKey(orgSlug, sourceId ?? "", q),
    // The display order is fixed here, per query, so cached and
    // placeholder data (``keepPreviousData`` while the next query loads)
    // always keep the order of the query that produced them: a search
    // keeps the backend's relevance ranking (exact name first, so "a2"
    // puts appli/a2 at the top), the unsearched list is alphabetical.
    queryFn: async () =>
      orderCodeSourceProjects(
        (
          await api.get<CodeSourceProject[]>(
            `/orgs/${orgSlug}/code-sources/${sourceId}/projects`,
            // limit=500 matches the backend's typed-search ceiling.
            // The picker is the only consumer of this endpoint and
            // users expect "all matches", not a first-N truncation.
            { params: { q, limit: 500 } },
          )
        ).data,
        q,
      ),
    enabled: Boolean(orgSlug) && Boolean(sourceId),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCodeSourceBranches(
  orgSlug: string,
  sourceId: string | null,
  projectId: string | null,
  q: string,
) {
  return useQuery({
    queryKey: codeSourceBranchesKey(
      orgSlug,
      sourceId ?? "",
      projectId ?? "",
      q,
    ),
    queryFn: async () =>
      (
        await api.get<CodeSourceBranch[]>(
          `/orgs/${orgSlug}/code-sources/${sourceId}/projects/${encodeURIComponent(
            projectId ?? "",
          )}/branches`,
          { params: { q, limit: 500 } },
        )
      ).data,
    enabled:
      Boolean(orgSlug) && Boolean(sourceId) && Boolean(projectId),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}
